import { useCallback, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'
import { IS_DESKTOP_EXPERIENCE } from '../lib/deviceGate'
import { emitHubTool, useHubToolEvents } from '../lib/hubTools'

const COLLAPSED_WIDTH_REM = 11.5
const COLLAPSED_GAP_REM = 0.4
const EXPANDED_WIDTH_REM = 17
const LAYOUT_PAD = 8

let collapsedIds = []
const EMPTY_DOCK = []
const dockListeners = new Set()

const emitDock = () => {
  dockListeners.forEach((listener) => listener())
}

const subscribeHubToolDock = (listener) => {
  dockListeners.add(listener)
  return () => dockListeners.delete(listener)
}

const getHubToolDockSnapshot = () => collapsedIds

const setHubToolCollapsed = (id, collapsed) => {
  if (!id) return
  const has = collapsedIds.includes(id)
  if (collapsed && !has) {
    collapsedIds = [...collapsedIds, id]
    emitDock()
    return
  }
  if (!collapsed && has) {
    collapsedIds = collapsedIds.filter((item) => item !== id)
    emitDock()
  }
}

const readRemPx = () => parseFloat(getComputedStyle(document.documentElement).fontSize) || 16

/** Screen-space chrome, dock, and sidebar from live layout — not CSS variable strings. */
const measureLayoutInsets = () => {
  const chromeEl = document.querySelector('.maya-chrome')
  const dockEl = document.querySelector('.site-bottom-dock')
  const sidebarEl = document.querySelector('.site-sidebar-scroll')

  const chromeRect = chromeEl?.getBoundingClientRect()
  const dockRect = dockEl?.getBoundingClientRect()
  const sidebarRect = sidebarEl?.getBoundingClientRect()

  const chrome = chromeRect?.height > 0 ? chromeRect.height : 0
  const dock = dockRect?.height > 0 ? dockRect.height : 0
  const sidebar =
    IS_DESKTOP_EXPERIENCE && sidebarRect?.width > 0 ? sidebarRect.width : 0

  return { chrome, dock, sidebar, pad: LAYOUT_PAD }
}

const getMaxPanelHeight = () => {
  const { chrome, dock, pad } = measureLayoutInsets()
  return Math.max(120, window.innerHeight - chrome - dock - pad * 2)
}

const clamp = (value, min, max) => Math.min(max, Math.max(min, value))

const HubToolWindow = ({
  toolId,
  label,
  titleClassName = 'text-white',
  borderClassName = 'border-white/30',
  defaultOpen = false,
  open,
  columnIndex = 0,
  anchor = 'left',
  children,
}) => {
  const [present, setPresent] = useState(defaultOpen)
  const [collapsed, setCollapsed] = useState(false)
  const [dragged, setDragged] = useState(false)
  const [position, setPosition] = useState({ left: 0, top: 0 })
  const positionRef = useRef(position)
  const draggedRef = useRef(dragged)
  const [dragging, setDragging] = useState(false)
  const [maxPanelHeight, setMaxPanelHeight] = useState(() => getMaxPanelHeight())
  const [layoutInsets, setLayoutInsets] = useState(measureLayoutInsets)
  const [remPx, setRemPx] = useState(readRemPx)
  const panelRef = useRef(null)
  const dragRef = useRef(null)
  const dockIds = useSyncExternalStore(
    subscribeHubToolDock,
    getHubToolDockSnapshot,
    () => EMPTY_DOCK
  )

  const constrainPosition = useCallback((left, top, width, height) => {
    const { chrome, dock, sidebar, pad } = measureLayoutInsets()
    return {
      left: clamp(left, sidebar + pad, Math.max(sidebar + pad, window.innerWidth - width - pad)),
      top: clamp(top, chrome + pad, Math.max(chrome + pad, window.innerHeight - dock - height - pad)),
    }
  }, [])

  useLayoutEffect(() => {
    positionRef.current = position
  }, [position])

  useLayoutEffect(() => {
    draggedRef.current = dragged
  }, [dragged])

  const reconcilePanelLayout = useCallback(() => {
    const insets = measureLayoutInsets()
    setLayoutInsets((prev) => {
      if (
        prev.chrome === insets.chrome
        && prev.dock === insets.dock
        && prev.sidebar === insets.sidebar
        && prev.pad === insets.pad
      ) {
        return prev
      }
      return insets
    })
    const nextRem = readRemPx()
    setRemPx((prev) => (prev === nextRem ? prev : nextRem))
    setMaxPanelHeight(getMaxPanelHeight())

    if (!panelRef.current || collapsed) return

    const panel = panelRef.current
    const rect = panel.getBoundingClientRect()
    if (rect.width <= 0 || rect.height <= 0) return

    const { chrome, dock, sidebar, pad } = insets
    const minTop = chrome + pad
    const maxBottom = window.innerHeight - dock - pad
    const minLeft = sidebar + pad
    const maxRight = window.innerWidth - pad

    const overflows =
      rect.top < minTop - 0.5
      || rect.bottom > maxBottom + 0.5
      || rect.left < minLeft - 0.5
      || rect.right > maxRight + 0.5

    const useDraggedPosition = draggedRef.current || overflows
    const sourceLeft = draggedRef.current ? positionRef.current.left : rect.left
    const sourceTop = draggedRef.current ? positionRef.current.top : rect.top
    const next = constrainPosition(sourceLeft, sourceTop, rect.width, rect.height)

    if (!useDraggedPosition) return

    if (!draggedRef.current) {
      setDragged(true)
    }

    setPosition((prev) => {
      if (prev.left === next.left && prev.top === next.top) return prev
      return next
    })
  }, [collapsed, constrainPosition])

  useLayoutEffect(() => {
    if (!present || collapsed) return undefined

    reconcilePanelLayout()

    const panel = panelRef.current
    const cleanups = []

    if (panel && typeof ResizeObserver !== 'undefined') {
      const panelObserver = new ResizeObserver(reconcilePanelLayout)
      panelObserver.observe(panel)
      cleanups.push(() => panelObserver.disconnect())
    }

    const chromeEl = document.querySelector('.maya-chrome')
    const dockEl = document.querySelector('.site-bottom-dock')
    const sidebarEl = document.querySelector('.site-sidebar-scroll')
    if (typeof ResizeObserver !== 'undefined') {
      const layoutObserver = new ResizeObserver(reconcilePanelLayout)
      if (chromeEl) layoutObserver.observe(chromeEl)
      if (dockEl) layoutObserver.observe(dockEl)
      if (sidebarEl) layoutObserver.observe(sidebarEl)
      cleanups.push(() => layoutObserver.disconnect())
    }

    window.addEventListener('resize', reconcilePanelLayout)
    cleanups.push(() => window.removeEventListener('resize', reconcilePanelLayout))

    return () => {
      cleanups.forEach((cleanup) => cleanup())
    }
  }, [collapsed, present, reconcilePanelLayout])

  useLayoutEffect(() => {
    if (open === undefined) return
    setPresent(open)
    if (open) setCollapsed(false)
  }, [open])

  useLayoutEffect(() => {
    setHubToolCollapsed(toolId, present && collapsed)
    return () => setHubToolCollapsed(toolId, false)
  }, [toolId, present, collapsed])

  useLayoutEffect(() => {
    if (!present || !toolId) return
    emitHubTool(toolId, 'open')
  }, [present, toolId])

  const handleOpen = useCallback(() => {
    setPresent(true)
    setCollapsed(false)
  }, [])

  const applyClose = useCallback(() => {
    setPresent(false)
    setCollapsed(false)
    setHubToolCollapsed(toolId, false)
  }, [toolId])

  const handleClose = useCallback((event) => {
    event?.stopPropagation?.()
    applyClose()
    emitHubTool(toolId, 'close')
  }, [applyClose, toolId])

  const handleHide = useCallback((event) => {
    event?.stopPropagation?.()
    setCollapsed(true)
  }, [])

  const handleExpand = useCallback(() => {
    setCollapsed(false)
  }, [])

  const handleOpenHidden = useCallback(() => {
    setPresent(true)
    setCollapsed(true)
  }, [])

  const handleLoadDefaults = useCallback(() => {
    setDragged(false)
    setPosition({ left: 0, top: 0 })
  }, [])

  useHubToolEvents(toolId, {
    open: handleOpen,
    openHidden: handleOpenHidden,
    close: applyClose,
    loadDefaults: handleLoadDefaults,
  })

  const dockIndex = dockIds.indexOf(toolId)
  const collapsedSlot = dockIndex >= 0 ? dockIndex : dockIds.length

  const handleTitlePointerDown = (event) => {
    if (event.button !== 0) return
    if (event.target.closest('button')) return
    if (collapsed) return

    const panel = panelRef.current
    if (!panel) return
    const rect = panel.getBoundingClientRect()
    dragRef.current = {
      pointerId: event.pointerId,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      width: rect.width,
      height: rect.height,
      moved: false,
    }
    setDragging(true)
    event.currentTarget.setPointerCapture(event.pointerId)
    event.preventDefault()
  }

  const handleTitlePointerMove = (event) => {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== event.pointerId) return
    drag.moved = true
    const next = constrainPosition(
      event.clientX - drag.offsetX,
      event.clientY - drag.offsetY,
      drag.width,
      drag.height
    )
    setDragged(true)
    setPosition(next)
  }

  const handleTitlePointerUp = (event) => {
    if (!dragRef.current || dragRef.current.pointerId !== event.pointerId) return
    dragRef.current = null
    setDragging(false)
    try {
      event.currentTarget.releasePointerCapture(event.pointerId)
    } catch {
      // Capture may already be released
    }
  }

  const handleTitleClick = () => {
    if (collapsed) handleExpand()
  }

  const handleTitleKeyDown = (event) => {
    if (!collapsed) return
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    handleExpand()
  }

  if (!present) return null

  const { dock, sidebar, pad } = layoutInsets

  const defaultStyle = dragged
    ? { left: position.left, top: position.top }
    : anchor === 'right'
      ? {
          right: pad + columnIndex * 17.75 * remPx,
          bottom: dock + pad,
        }
      : {
          left: sidebar + pad + columnIndex * 18 * remPx,
          bottom: dock + pad,
        }

  const collapsedStyle = {
    left: (IS_DESKTOP_EXPERIENCE ? sidebar : 0) + 0.5 * remPx + collapsedSlot * (COLLAPSED_WIDTH_REM + COLLAPSED_GAP_REM) * remPx,
    bottom: dock + 0.35 * remPx,
    width: `${COLLAPSED_WIDTH_REM}rem`,
  }

  const panel = (
    <div
      ref={panelRef}
      className={`hub-tool-panel pointer-events-auto fixed flex flex-col overflow-hidden border bg-black/80 text-white shadow-lg backdrop-blur-sm ${borderClassName} ${
        collapsed ? 'is-collapsed z-[calc(var(--z-floating-tools)+1)] rounded-md' : 'z-[var(--z-floating-tools)] rounded-xl'
      } ${dragging ? 'is-dragging' : ''}`}
      style={{
        width: collapsed ? undefined : `${EXPANDED_WIDTH_REM}rem`,
        maxHeight: collapsed ? undefined : maxPanelHeight,
        ...(collapsed ? collapsedStyle : defaultStyle),
      }}
      aria-label={`${label} tuning panel`}
      aria-expanded={!collapsed}
    >
      <div
        className={`z-[calc(var(--z-floating-tools)+10)] flex shrink-0 select-none items-center gap-1 touch-none bg-black/95 ${
          collapsed
            ? 'cursor-pointer px-2 py-1.5'
            : 'cursor-grab border-b border-white/10 px-3 py-2 active:cursor-grabbing'
        } ${dragging ? 'cursor-grabbing' : ''}`}
        onPointerDown={handleTitlePointerDown}
        onPointerMove={handleTitlePointerMove}
        onPointerUp={handleTitlePointerUp}
        onPointerCancel={handleTitlePointerUp}
        onClick={handleTitleClick}
        onKeyDown={handleTitleKeyDown}
        role="toolbar"
        tabIndex={collapsed ? 0 : -1}
        aria-label={collapsed ? `Restore ${label} panel` : `Move ${label} panel`}
      >
        <p className={`min-w-0 flex-1 truncate text-xs font-medium uppercase tracking-wide ${titleClassName}`}>
          {label}
        </p>
        {collapsed ? null : (
          <button
            type="button"
            onClick={handleHide}
            onPointerDown={(event) => event.stopPropagation()}
            className="rounded px-2 py-1 text-xs text-white/80 hover:bg-white/10 hover:text-white"
            aria-label={`Hide ${label} panel`}
          >
            Hide
          </button>
        )}
        <button
          type="button"
          onClick={handleClose}
          onPointerDown={(event) => event.stopPropagation()}
          className="grid h-5 w-5 shrink-0 place-items-center rounded text-[15px] font-bold leading-none text-red-500 hover:bg-red-500/20 hover:text-red-400"
          aria-label={`Remove ${label} from scene`}
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>
      {collapsed ? null : (
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain">
          {children}
        </div>
      )}
    </div>
  )

  return createPortal(panel, document.body)
}

export default HubToolWindow
